import React from 'react';
import { Folder, FolderCog, Check, Loader2, Circle, CircleDot, Settings, Trash2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useWorkbookQuery } from '@/hooks/content-generator/use-workbook-query';
import { useAddPageToProject } from '@/hooks/private-text-generator/useAddPageToProject';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { motion } from 'framer-motion';

interface PageAddToProjectButtonProps {
  pageId: string;
  forceWhiteText?: boolean;
  dropdownLabel?: string;
  variant?: 'default' | 'auto';
  currentProjectId?: string | null; // Pass known project ID to avoid API calls
  isOwner?: boolean; // Pass ownership state to control UI behavior
}

const PageAddToProjectButton: React.FC<PageAddToProjectButtonProps> = ({ 
  pageId, 
  forceWhiteText, 
  dropdownLabel = "Add to Project",
  variant = 'auto',
  currentProjectId: propCurrentProjectId,
  isOwner: propIsOwner
}) => {
  const location = useLocation();
  const { data: workbookData, isLoading: isLoadingProjects } = useWorkbookQuery({ 
    filterByStatus: 'all' // Get all non-archived projects
  });
  
  // Use the page add to project hook
  const {
    currentProjectId: hookCurrentProjectId,
    updatePageProject,
    removeFromProject,
    isPending,
    isOwner: hookIsOwner
  } = useAddPageToProject(pageId);
  
  // Use passed currentProjectId if available, otherwise fall back to hook
  const currentProjectId = propCurrentProjectId !== undefined ? propCurrentProjectId : hookCurrentProjectId;
  
  // Use passed isOwner if available, otherwise fall back to hook
  const effectiveIsOwner = propIsOwner !== undefined ? propIsOwner : hookIsOwner;
  
  const projects = workbookData?.projects || [];

  // Determine button styling based on context and state
  const getButtonStyling = () => {
    return {
      bgClass: "bg-white/20 dark:bg-black/20",
      borderClass: "border-white/20 dark:border-transparent",
      iconColor: forceWhiteText ? "text-white" : "text-gray-700 dark:text-white",
      shadow: "shadow-lg"
    };
  };

  const styling = getButtonStyling();

  const handleProjectSelect = async (projectId: string | null) => {
    if (isPending) return;
    await updatePageProject(projectId);
  };

  const handleRemoveFromProject = async () => {
    if (isPending) return;
    await removeFromProject();
  };
  
  const isAnythingLoading = isLoadingProjects;
  const isItemInAnyProject = currentProjectId !== null;

  // Default icon variant (for card buttons)
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <motion.button
            className={cn(
              "inline-flex items-center justify-center p-1 rounded-full",
              styling.bgClass,
              "border",
              styling.borderClass,
              styling.shadow,
              "backdrop-blur-lg"
            )}
            onClick={(e) => e.stopPropagation()}
            disabled={isPending}
          >
            {isItemInAnyProject ? (
              <FolderCog size={20} className={styling.iconColor} />
            ) : (
              <Folder size={20} className={styling.iconColor} />
            )}
          </motion.button>
        </DropdownMenuTrigger>

        <DropdownMenuContent className="w-80" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuLabel>{dropdownLabel}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {isAnythingLoading ? (
            <div className="flex items-center justify-center p-2">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : effectiveIsOwner === false ? (
            // Show informative message for non-owners
            <div className="px-2 py-3">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <Settings className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                    Permission Required
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                    Only the user who created this page can modify its project assignments.
                  </p>
                </div>
              </div>
            </div>
          ) :
            // Show project list for owners
            <>
              {workbookData?.projects?.map((project) => {
                const isCurrentProject = currentProjectId === project.id;
                const isDisabled = isPending;
                
                return (
                  <DropdownMenuItem
                    key={project.id}
                    onClick={() => {
                      if (isDisabled) return;
                      
                      if (isCurrentProject) {
                        // Currently in this project, remove from it
                        removeFromProject?.();
                      } else {
                        // Not in this project, add to it
                        updatePageProject(project.id);
                      }
                    }}
                    disabled={isDisabled}
                    className="flex items-center gap-3 p-3"
                  >
                    <div className="flex items-center justify-center w-4 h-4">
                      {isCurrentProject ? (
                        <CircleDot className="h-4 w-4 text-blue-600" />
                      ) : (
                        <Circle className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                    <span className="flex-1 truncate text-sm">
                      {project.title}
                    </span>
                    {isCurrentProject && (
                      <Check className="h-4 w-4 text-green-600" />
                    )}
                  </DropdownMenuItem>
                );
              })}
              
              {/* Remove from project option */}
              {currentProjectId && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      if (!isPending) {
                        removeFromProject?.();
                      }
                    }}
                    disabled={isPending}
                    className="flex items-center gap-3 p-3 text-red-600 dark:text-red-400"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="text-sm">Remove from Project</span>
                  </DropdownMenuItem>
                </>
              )}
            </>
          }
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};

export default PageAddToProjectButton; 